import {
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { Chat } from "@/components/Chat";
import { useEvent } from "@/components/hooks/useEvent";
import {
  campaignQueryOptions,
  getCurrentUserQueryOptions,
  getInboxForFeedQueryOptions,
  markMessageAsRead,
  sendCampaginMessage,
} from "@/lib/api";

/** THiS IS FOR CAMPAIGN RUNNERS */
export const Route = createFileRoute(
  "/app/_sidebar-layout/campaigns/$campaignId/view/$algoId/_layout/messages",
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { campaignId, algoId },
  }) => {
    const campaign = await queryClient.ensureQueryData(
      campaignQueryOptions(parseInt(campaignId)),
    );
    const algo = campaign.algorithms.find((a) => a.id === parseInt(algoId))!;

    if (algo.details.state === "proposed") {
      throw redirect({
        to: `/app/campaigns/$campaignId/review/$algoId`,
        params: { campaignId, algoId },
      });
    } else if (algo.details.state === "denied") {
      throw redirect({ to: "/app/campaigns" });
    }

    await Promise.all([
      queryClient.ensureQueryData(getCurrentUserQueryOptions()),
      queryClient.ensureInfiniteQueryData(
        getInboxForFeedQueryOptions({
          campaignId: parseInt(campaignId),
          feedId: parseInt(algoId),
          limit: 10,
        }),
      ),
    ]);
  },
});

function RouteComponent() {
  const { campaignId, algoId } = Route.useParams();
  const { data: algo } = useSuspenseQuery(
    campaignQueryOptions(parseInt(algoId)),
  );
  const { data, fetchNextPage, isLoading, hasNextPage } =
    useSuspenseInfiniteQuery(
      getInboxForFeedQueryOptions({
        campaignId: parseInt(campaignId),
        feedId: parseInt(algoId),
        limit: 10,
      }),
    );

  const flattenedMessages = data.pages.flatMap((page) => page.data).reverse();

  const sendMessage = useEvent(async (message: string) => {
    await sendCampaginMessage({
      recipient_id: algo.user_id,
      body: message,
      context: {
        algo_id: parseInt(algoId),
        campaign_id: parseInt(campaignId),
      },
    });
  });

  const onMarkMessageAsRead = useEvent(async (messageId: number) => {
    await markMessageAsRead(messageId, {
      type: "campaign",
      campaign_id: parseInt(campaignId),
      algo_id: parseInt(algoId),
    });
  });

  return (
    <Chat
      onSendMessage={sendMessage}
      onLoadNextPage={async () => {
        await fetchNextPage();
      }}
      onMarkMessageAsRead={(messageId) => {
        void onMarkMessageAsRead(messageId);
      }}
      isLoadingNextPage={isLoading}
      hasNextPage={hasNextPage}
      messages={flattenedMessages}
    />
  );
}
